<template>
  <div class="audio-player">
    <div class="player-wrap">
      <div class="playback">
        <button type="button" class="amplitude-play-pause">
          <PlayIcon class="play-icon" />
          <PauseIcon class="pause-icon" />
        </button>
      </div>
      <div class="waves">
        <div class="amplitude-wave-form"></div>
        <div class="visual-elements">
          <input type="range" class="amplitude-song-slider" step=".1" />
          <input
            type="range"
            class="visual-element"
            :class="{ 'is-exceeding': getIsExceeding(visualElement) }"
            step=".1"
            min="0"
            :max="duration"
            v-for="visualElement of visualElements.slice().reverse()"
            :key="visualElement._id"
            :value="visualElement.time"
            @input="updateVisualElement($event, visualElement._id)"
            @change="$emit('reorder')"
          />
        </div>
        <span class="current-time" :style="timeStyle">
          <div class="text-center" style="width:150px">
            <input
              type="text"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              maxlength="2"
              class="placeholder-black text-center w-5"
              @click="editTime"
              v-model="inputHours"
              @keyup.enter="enterTime"
              @blur="enterTime"
            />
            :
            <input
              type="text"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              maxlength="2"
              class="placeholder-black text-center w-5"
              @click="editTime"
              v-model="inputMinutes"
              @keyup.enter="enterTime"
              @blur="enterTime"
            />:
            <input
              type="text"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              maxlength="2"
              class="placeholder-black text-center w-5"
              @click="editTime"
              v-model="inputSeconds"
              @keyup.enter="enterTime"
              @blur="enterTime"
            />:
            <input
              type="text"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
              maxlength="2"
              class="placeholder-black text-center w-5"
              @click="editTime"
              v-model="inputMilliseconds"
              @keyup.enter="enterTime"
              @blur="enterTime"
            />
          </div>
        </span>
        <div class="visual-element-balloons">
          <span
            class="balloon"
            v-for="(balloon, i) of visualElementsBalloons"
            :key="balloon._id"
            :class="{ 'is-exceeding': balloon.isExceeding }"
            :style="{
              left: `${balloon.left}%`,
              'z-index': 10 + visualElementsBalloons.length - i,
            }"
            >{{ i + 1 }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Amplitude from 'amplitudejs'
import PlayIcon from '@/assets/icons/icon-play.svg'
import PauseIcon from '@/assets/icons/icon-pause.svg'

export default {
  components: {
    PlayIcon,
    PauseIcon,
  },

  props: {
    src: {
      type: String,
      required: true,
    },

    visualElements: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      timePercent: 0,
      duration: 0,
      currentTime: 0,
      inputHours: '00',
      inputMinutes: '00',
      inputSeconds: '00',
      inputMilliseconds: '00',
    }
  },

  computed: {
    timeStyle() {
      return `left: ${this.timePercent}%; pointer-events: auto;`
    },
    visualElementsBalloons() {
      return this.visualElements.map(visualElement => {
        return {
          _id: visualElement._id,
          left: Math.min(
            (visualElement.time * 100) / (this.duration || 1),
            100,
          ),
          isExceeding: this.getIsExceeding(visualElement),
        }
      })
    },
    visualElementsModel: {
      get() {
        return this.visualElements.map(visualElement => visualElement.time)
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },

  mounted() {
    Amplitude.init({
      continue_next: false,
      songs: [
        {
          url: this.src,
        },
      ],
      waveforms: {
        sample_rate: 300,
      },
    })
    this.audioEl = Amplitude.getAudio()

    this.audioEl.addEventListener('timeupdate', this.updateTimePosition)
    this.audioEl.addEventListener('seeking', this.updateTimePosition)
    this.audioEl.addEventListener('loadedmetadata', this.saveDuration)
  },

  beforeDestroy() {
    this.audioEl.addEventListener('timeupdate', this.updateTimePosition)
    this.audioEl.removeEventListener('seeking', this.updateTimePosition)
    this.audioEl.removeEventListener('loadedmetadata', this.saveDuration)

    Amplitude.pause()
  },

  methods: {
    enterTime() {
      const timeInserted =
        parseInt(this.inputHours) * 60 * 60 +
        parseInt(this.inputMinutes) * 60 +
        parseInt(this.inputSeconds) +
        parseInt(this.inputMilliseconds) / 100

      if (timeInserted <= this.audioEl.duration) {
        this.audioEl.currentTime = timeInserted
      } else {
        alert(
          'Il tempo inserito deve essere minore o uguale alla massima lunghezza della traccia audio',
        )
      }
    },
    editTime(event) {
      event.stopPropagation()
    },
    timeSplit(time) {
      const pad = n => (n < 10 ? `0${n}` : n)
      const hours = Math.floor(time / (60 * 60))
      const minutes = Math.floor((time % (60 * 60)) / 60)
      const seconds = Math.floor((time % (60 * 60)) % 60)
      const milliseconds = Math.floor((time % 1) * 100)
      return [pad(hours), pad(minutes), pad(seconds), pad(milliseconds)]
    },
    updateTimePosition() {
      this.currentTime = this.audioEl.currentTime
      const timeSplits = this.timeSplit(this.currentTime)
      this.inputHours = timeSplits[0]
      this.inputMinutes = timeSplits[1]
      this.inputSeconds = timeSplits[2]
      this.inputMilliseconds = timeSplits[3]

      this.timePercent = (this.currentTime * 100) / this.audioEl.duration || 0
      this.$emit('time', this.currentTime)
    },
    saveDuration() {
      this.duration = this.audioEl.duration
      this.$emit('durationSaved', this.duration)
    },
    updateVisualElement(event, id) {
      this.$emit('move', {
        time: Math.round(event.target.value),
        id,
      })
    },
    getIsExceeding(visualElement) {
      return this.duration && visualElement.time > Math.ceil(this.duration)
    },
  },
}
</script>

<style lang="scss" scoped>
.audio-player {
  @apply pb-16;

  .player-wrap {
    @apply flex h-28;

    .playback {
      @apply flex items-center justify-center h-full w-23 bg-gray-900;

      .amplitude-play-pause {
        @apply w-13 h-13 bg-white rounded-full text-gray-900 outline-none;

        svg {
          @apply inline-block w-6 h-auto;
        }

        &.amplitude-paused {
          .pause-icon {
            display: none;
          }
        }

        &.amplitude-playing {
          .play-icon {
            display: none;
          }
        }
      }
    }

    .waves {
      @apply relative flex-grow h-full bg-gray-200;

      .amplitude-wave-form {
        @apply h-full relative z-0 overflow-hidden;

        /deep/ svg {
          @apply h-full w-full;
          stroke: theme('colors.gray.400');
          stroke-width: 2px;
          // transform: scaleY(2);
        }
      }

      .visual-elements {
        @apply absolute inset-0;
        z-index: 5;

        input[type='range'] {
          @apply block w-full h-full absolute top-0 left-0;
          pointer-events: none;
          -webkit-appearance: none;
          background: transparent;

          &::-ms-track {
            width: 100%;
            cursor: pointer;
            background: transparent;
            border-color: transparent;
            color: transparent;
          }

          &.visual-element {
            left: -11.5px;
            width: calc(100% + 23px);

            @mixin slider-thumb {
              -webkit-appearance: none;
              @apply mt-2 z-10;
              width: 23px;
              height: 151px;
              cursor: grab;
              pointer-events: all;
              background-image: url("data:image/svg+xml,%3Csvg width='23px' height='151px' viewBox='0 0 23 151' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg transform='translate(-495.000000, -285.000000)'%3E%3Cg transform='translate(495.000000, 285.000000)'%3E%3Cline x1='11.75078' y1='0' x2='11.75078' y2='140' id='Line-3-Copy' stroke='%23252525'%3E%3C/line%3E%3Cellipse fill='%23252525' fill-rule='nonzero' cx='11.5' cy='140' rx='11.5' ry='11'%3E%3C/ellipse%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }

            &::-webkit-slider-thumb {
              @include slider-thumb;

              &::before {
                content: 'x';
              }
            }
            &::-moz-range-thumb {
              @include slider-thumb;
            }
            &::-ms-thumb {
              @include slider-thumb;
            }
            &.is-exceeding::-webkit-slider-thumb {
              background-image: url("data:image/svg+xml,%3Csvg width='23px' height='151px' viewBox='0 0 23 151' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg transform='translate(-495.000000, -285.000000)'%3E%3Cg transform='translate(495.000000, 285.000000)'%3E%3Cline x1='11.75078' y1='0' x2='11.75078' y2='140' id='Line-3-Copy' stroke='%23e02020'%3E%3C/line%3E%3Cellipse fill='%23e02020' fill-rule='nonzero' cx='11.5' cy='140' rx='11.5' ry='11'%3E%3C/ellipse%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }
            &.is-exceeding::-moz-range-thumb {
              background-image: url("data:image/svg+xml,%3Csvg width='23px' height='151px' viewBox='0 0 23 151' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg transform='translate(-495.000000, -285.000000)'%3E%3Cg transform='translate(495.000000, 285.000000)'%3E%3Cline x1='11.75078' y1='0' x2='11.75078' y2='140' id='Line-3-Copy' stroke='%23e02020'%3E%3C/line%3E%3Cellipse fill='%23e02020' fill-rule='nonzero' cx='11.5' cy='140' rx='11.5' ry='11'%3E%3C/ellipse%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }
            &.is-exceeding::-ms-thumb {
              background-image: url("data:image/svg+xml,%3Csvg width='23px' height='151px' viewBox='0 0 23 151' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg transform='translate(-495.000000, -285.000000)'%3E%3Cg transform='translate(495.000000, 285.000000)'%3E%3Cline x1='11.75078' y1='0' x2='11.75078' y2='140' id='Line-3-Copy' stroke='%23e02020'%3E%3C/line%3E%3Cellipse fill='%23e02020' fill-rule='nonzero' cx='11.5' cy='140' rx='11.5' ry='11'%3E%3C/ellipse%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }
          }

          &.amplitude-song-slider {
            @apply z-0;
            pointer-events: all;
            left: -2px;
            width: calc(100% + 4px);

            @mixin slider-thumb {
              -webkit-appearance: none;
              @apply h-28 bg-blue-500 z-20;
              width: 4px;
              cursor: grab;
              pointer-events: all;
            }

            &::-webkit-slider-thumb {
              @include slider-thumb;
            }
            &::-moz-range-thumb {
              @include slider-thumb;
            }
            &::-ms-thumb {
              @include slider-thumb;
            }
          }
        }
      }

      .current-time {
        @apply absolute mt-6 transform -translate-x-1/2 pointer-events-none;
        top: 100%;
        font-size: 1rem;
      }

      .visual-element-balloons {
        @apply absolute left-0 right-0;
        top: 100%;

        .balloon {
          @apply absolute top-0 flex justify-center items-center w-4 h-4;
          @apply bg-black rounded-full text-white font-bold text-sm;
          @apply transform  -translate-x-1/2 -translate-y-1/2 pointer-events-none;
          z-index: 11;
          cursor: grab;

          &.is-exceeding {
            @apply bg-error text-white;
          }
        }
      }
    }
  }
}
</style>
